import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import { algoliasearch } from "algoliasearch";
import { db } from "../firebaseConfig";
import {
  deriveInvoiceRaised,
  evaluateProjectStatuses,
} from "../utils/status";
import { buildTasksSearchText } from "../utils/tasksSearchText";
import { buildNumericAmountFields } from "../utils/numericFields";
import {
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY,
  ALGOLIA_INDEX_NAME,
  ALGOLIA_FACETS,
  SORT_INDEXES,
  buildUiFiltersString,
  buildNumericFilters,
} from "../utils/algoliaConfig";
import {
  PREDEFINED_BILLING_ENTITIES,
  PREDEFINED_PAYMENT_STATUSES,
  PREDEFINED_PROJECT_MANAGERS,
  PREDEFINED_PROJECT_STATUSES,
  PREDEFINED_TASK_TYPES,
  buildProjectFilterMetadata,
  createDefaultProjectFilters,
  createEmptyProjectFilterOptions,
  filterProjects as filterProjectsLocally,
  hasLocalOnlyProjectFilters,
  sortProjects as sortProjectsLocally,
} from "../utils/projectFiltering";

const PAGE_SIZE = 20;

const AMOUNT_FIELD_NAMES = new Set(["amountIncVat", "cost", "income", "vat"]);

const normalizeFacetCounts = (facets = {}) => ({
  ...facets,
  billingEntity: facets.billingEntity || facets.company || {},
});

// Helper to convert facets response to filter options
const optionsFromFacets = (facets, selectedFilters = {}) => {
  const getKeys = (k) =>
    facets && facets[k] ? Object.keys(facets[k]).filter(Boolean).sort() : [];

  // Merge predefined values with facet values, keeping predefined ones first
  const mergeWithPredefined = (predefined, facetKeys, selectedValues = []) => {
    const additionalKeys = facetKeys.filter((k) => !predefined.includes(k));
    return [
      ...predefined,
      ...additionalKeys.sort(),
      ...selectedValues.filter(
        (value) => value && !predefined.includes(value) && !additionalKeys.includes(value)
      ),
    ];
  };

  return {
    ...createEmptyProjectFilterOptions(),
    client: mergeWithPredefined([], getKeys("client"), selectedFilters.client || []),
    billingEntity: mergeWithPredefined(
      PREDEFINED_BILLING_ENTITIES,
      getKeys("company"),
      selectedFilters.billingEntity || []
    ),
    country: selectedFilters.country || [],
    projectManager: mergeWithPredefined(
      PREDEFINED_PROJECT_MANAGERS,
      getKeys("projectManager"),
      selectedFilters.projectManager || []
    ),
    projectStatus: mergeWithPredefined(
      PREDEFINED_PROJECT_STATUSES,
      getKeys("projectStatus"),
      selectedFilters.projectStatus || []
    ),
    paymentStatus: mergeWithPredefined(
      PREDEFINED_PAYMENT_STATUSES,
      getKeys("paymentStatus"),
      selectedFilters.paymentStatus || []
    ),
    taskType: PREDEFINED_TASK_TYPES,
    poc: mergeWithPredefined([], getKeys("poc"), selectedFilters.poc || []),
  };
};

const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filters, setFilters] = useState(createDefaultProjectFilters);
  const [availableFilterOptions, setAvailableFilterOptions] = useState(
    createEmptyProjectFilterOptions
  );
  const [filterCounts, setFilterCounts] = useState({});
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpenState] = useState(false);
  const [allProjectsForFilters, setAllProjectsForFilters] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const activeQueryTokenRef = useRef("");
  const algoliaPageRef = useRef(0);
  const localFilteredProjectsRef = useRef([]);
  const localFacetMetadataRef = useRef(null);
  const allProjectsForFiltersRef = useRef(null);
  const allProjectsForFiltersPromiseRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const searchDebounceTimerRef = useRef(null);
  const isFilterPanelOpenRef = useRef(false);
  const needsFacetRefreshRef = useRef(false);
  const filterCountsRef = useRef(filterCounts);
  const refreshFacetCountsNowRef = useRef(null);

  // Load filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem("projectFilters");
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters({ ...createDefaultProjectFilters(), ...parsed });
      } catch (e) {
        console.error("Failed to parse saved filters:", e);
      }
    }
  }, []);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem("projectFilters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    filterCountsRef.current = filterCounts;
  }, [filterCounts]);

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [search]);

  const algoliaClient = useMemo(() => {
    if (!ALGOLIA_APP_ID || !ALGOLIA_SEARCH_API_KEY) return null;
    try {
      return algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);
    } catch (e) {
      console.error("Failed to init Algolia client:", e);
      return null;
    }
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((arr) => arr && arr.length > 0),
    [filters]
  );

  const requiresLocalFiltering = useMemo(
    () => hasLocalOnlyProjectFilters(filters),
    [filters]
  );

  const currentAlgoliaIndex = useMemo(() => {
    const key = `${sortColumn}:${sortOrder}`;
    return SORT_INDEXES[key] || ALGOLIA_INDEX_NAME;
  }, [sortColumn, sortOrder]);

  const useAlgolia = useMemo(() => {
    if (!algoliaClient) return false;
    const hasSearch = debouncedSearch.trim().length > 0;
    const hasNonDefaultSort =
      sortColumn !== "createdAt" || sortOrder !== "desc";
    return hasSearch || hasActiveFilters || hasNonDefaultSort;
  }, [algoliaClient, debouncedSearch, hasActiveFilters, sortColumn, sortOrder]);

  const queryMode = useMemo(() => {
    if (requiresLocalFiltering) return "local";
    return useAlgolia ? "algolia" : "firestore";
  }, [requiresLocalFiltering, useAlgolia]);

  // Fetch initial facet counts from Algolia
  useEffect(() => {
    if (!algoliaClient) return;

    const fetchFacets = async () => {
      try {
        const res = await algoliaClient.searchSingleIndex({
          indexName: ALGOLIA_INDEX_NAME,
          searchParams: {
            query: "",
            hitsPerPage: 0,
            facets: ALGOLIA_FACETS,
            maxValuesPerFacet: 500,
          },
        });

        const facets = normalizeFacetCounts(res?.facets || {});
        setAvailableFilterOptions(optionsFromFacets(facets));
        setFilterCounts(facets);
      } catch (e) {
        console.error("Failed to fetch facet counts:", e);
      }
    };

    fetchFacets();
  }, [algoliaClient]);

  const buildQueryToken = useCallback(() => {
    const filterToken = Object.entries(filters)
      .map(([key, values]) => `${key}:${values.join(",")}`)
      .join("|");
    return [
      debouncedSearch.trim(),
      sortColumn,
      sortOrder,
      queryMode,
      filterToken,
    ].join("::");
  }, [debouncedSearch, filters, queryMode, sortColumn, sortOrder]);

  const buildCombinedFilters = useCallback(
    (omitFacet = null) => {
      const uiFiltersString = buildUiFiltersString(filters, {
        omitFacets: omitFacet ? [omitFacet] : [],
      });
      const numericFilters = buildNumericFilters(filters);
      const numericFiltersString = numericFilters?.join(" AND ");

      return [uiFiltersString, numericFiltersString]
        .filter(Boolean)
        .join(" AND ");
    },
    [filters]
  );

  const resetPagination = useCallback(() => {
    setProjects([]);
    setSelectedProjects([]);
    setLastDoc(null);
    algoliaPageRef.current = 0;
    localFilteredProjectsRef.current = [];
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
    setHasMore(false);
    setError("");
  }, []);

  const setIsFilterPanelOpen = useCallback((isOpen) => {
    isFilterPanelOpenRef.current = isOpen;
    setIsFilterPanelOpenState(isOpen);
  }, []);

  // Handler for toggling individual filter values
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters((prev) => {
      const current = prev[filterType] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [filterType]: next };
    });
  }, []);

  // Handler to clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters(createDefaultProjectFilters());
  }, []);

  const convertTimestampToDate = useCallback((timestamp) => {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (typeof timestamp === "string") {
      return new Date(timestamp);
    }
    return null;
  }, []);

  const mapSortToFirestoreField = useCallback(() => {
    switch (sortColumn) {
      case "createdAt":
        return "createdAt";
      case "projectManager":
        return "projectManager";
      case "client":
        return "client";
      case "amountIncVat":
        return "amountIncVat";
      default:
        return "createdAt";
    }
  }, [sortColumn]);

  const applyStatusUpdates = useCallback(async (project, now) => {
    const {
      updatedTasks,
      projectStatus: newProjectStatus,
      invoiceRaised: derivedInvoiceRaised,
      paymentStatus: derivedPaymentStatus,
    } = evaluateProjectStatuses(project, now);

    const originalTasks = project.tasks || [];
    const tasksChanged =
      originalTasks.length !== updatedTasks.length ||
      updatedTasks.some(
        (task, index) =>
          task.taskStatus !==
          (originalTasks[index] && originalTasks[index].taskStatus)
      );

    const projectRef = doc(db, "projects", project.id);
    const updates = {};

    if (tasksChanged) {
      updates.tasks = updatedTasks;
      updates.tasksSearchText = buildTasksSearchText(updatedTasks);
    }
    if (project.projectStatus !== newProjectStatus) {
      updates.projectStatus = newProjectStatus;
    }
    if (project.invoiceRaised !== derivedInvoiceRaised) {
      updates.invoiceRaised = derivedInvoiceRaised;
    }
    if (project.paymentStatus !== derivedPaymentStatus) {
      updates.paymentStatus = derivedPaymentStatus || null;
    }

    if (Object.keys(updates).length > 0) {
      await updateDoc(projectRef, updates);
    }

    return {
      ...project,
      projectStatus: newProjectStatus,
      tasks: updatedTasks.length > 0 ? updatedTasks : project.tasks || [],
      invoiceRaised: derivedInvoiceRaised,
      paymentStatus: derivedPaymentStatus,
    };
  }, []);

  const fetchAllProjectsWithStatuses = useCallback(async () => {
    const projectsSnapshot = await getDocs(query(collection(db, "projects")));
    const now = new Date();

    return Promise.all(
      projectsSnapshot.docs.map(async (projectDoc) => {
        const raw = { id: projectDoc.id, ...projectDoc.data() };
        return applyStatusUpdates(raw, now);
      })
    );
  }, [applyStatusUpdates]);

  const ensureAllProjectsForFilters = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && allProjectsForFiltersRef.current) {
      return allProjectsForFiltersRef.current;
    }

    if (allProjectsForFiltersPromiseRef.current) {
      return allProjectsForFiltersPromiseRef.current;
    }

    allProjectsForFiltersPromiseRef.current = fetchAllProjectsWithStatuses()
      .then((allProjects) => {
        allProjectsForFiltersRef.current = allProjects;
        setAllProjectsForFilters(allProjects);
        return allProjects;
      })
      .catch((err) => {
        console.error("Failed to fetch all projects for filters:", err);
        return [];
      })
      .finally(() => {
        allProjectsForFiltersPromiseRef.current = null;
      });

    return allProjectsForFiltersPromiseRef.current;
  }, [fetchAllProjectsWithStatuses]);

  const localFacetMetadata = useMemo(() => {
    if (!allProjectsForFilters) return null;
    return buildProjectFilterMetadata(
      allProjectsForFilters,
      filters,
      debouncedSearch
    );
  }, [allProjectsForFilters, debouncedSearch, filters]);

  useEffect(() => {
    localFacetMetadataRef.current = localFacetMetadata;
  }, [localFacetMetadata]);

  useEffect(() => {
    if (!isFilterPanelOpen) return;
    ensureAllProjectsForFilters(true);
  }, [ensureAllProjectsForFilters, isFilterPanelOpen]);

  useEffect(() => {
    if (!isFilterPanelOpen || !localFacetMetadata) {
      return;
    }

    if (requiresLocalFiltering) {
      setFilterCounts(localFacetMetadata.counts);
      setAvailableFilterOptions(localFacetMetadata.options);
      return;
    }

    setFilterCounts((prev) => ({
      ...prev,
      country: localFacetMetadata.counts.country,
      taskType: localFacetMetadata.counts.taskType,
    }));
    setAvailableFilterOptions((prev) => ({
      ...prev,
      country: localFacetMetadata.options.country,
      taskType: localFacetMetadata.options.taskType,
    }));
  }, [
    isFilterPanelOpen,
    localFacetMetadata,
    requiresLocalFiltering,
  ]);

  const fetchLocalFilteredPage = useCallback(
    async (queryTokenParam = null, isInitialLoad = false) => {
      const queryToken = queryTokenParam || buildQueryToken();
      const isCurrentToken = activeQueryTokenRef.current === queryToken;

      if (isCurrentToken) {
        setError("");
        if (isInitialLoad) {
          setIsLoading(true);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        }
      }

      try {
        const allProjects = await fetchAllProjectsWithStatuses();
        const filteredProjects = sortProjectsLocally(
          filterProjectsLocally(allProjects, filters, debouncedSearch),
          sortColumn,
          sortOrder
        );

        if (activeQueryTokenRef.current === queryToken) {
          allProjectsForFiltersRef.current = allProjects;
          setAllProjectsForFilters(allProjects);
          localFilteredProjectsRef.current = filteredProjects;
          setProjects(filteredProjects.slice(0, PAGE_SIZE));
          setHasMore(filteredProjects.length > PAGE_SIZE);
          algoliaPageRef.current = 0;
          setLastDoc(null);
        }
      } catch (err) {
        console.error("Failed to filter projects locally:", err);
        if (activeQueryTokenRef.current === queryToken) {
          setError("Failed to fetch projects.");
        }
      } finally {
        if (activeQueryTokenRef.current === queryToken) {
          setIsLoading(false);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        }
      }
    },
    [
      buildQueryToken,
      debouncedSearch,
      fetchAllProjectsWithStatuses,
      filters,
      sortColumn,
      sortOrder,
    ]
  );

  const fetchFirestorePage = useCallback(
    async (cursor = null, queryTokenParam = null, isInitialLoad = false) => {
      // Firestore is only used when no filters are active (default view)
      // When filters are active, we use Algolia instead
      const sortField = mapSortToFirestoreField();
      const direction = sortOrder === "asc" ? "asc" : "desc";

      const constraints = [];
      constraints.push(orderBy(sortField, direction), limit(PAGE_SIZE));

      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      const queryToken = queryTokenParam || buildQueryToken();
      const isCurrentToken = activeQueryTokenRef.current === queryToken;
      if (isCurrentToken) {
        setError("");
        if (isInitialLoad) {
          setIsLoading(true);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        }
      }

      try {
        const projectsSnapshot = await getDocs(
          query(collection(db, "projects"), ...constraints)
        );

        const now = new Date();
        const hydrated = await Promise.all(
          projectsSnapshot.docs.map(async (projectDoc) => {
            const raw = { id: projectDoc.id, ...projectDoc.data() };
            return applyStatusUpdates(raw, now);
          })
        );

        if (activeQueryTokenRef.current === queryToken) {
          setProjects((prev) => (cursor ? [...prev, ...hydrated] : hydrated));

          const lastVisible =
            projectsSnapshot.docs[projectsSnapshot.docs.length - 1] || null;
          setLastDoc(lastVisible);
          setHasMore(projectsSnapshot.size === PAGE_SIZE);
        }
      } catch (err) {
        console.error("Failed to fetch projects from Firestore:", err);
        if (activeQueryTokenRef.current === queryToken) {
          setError("Failed to fetch projects.");
        }
      } finally {
        if (activeQueryTokenRef.current === queryToken) {
          setIsLoading(false);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        }
      }
    },
    [
      applyStatusUpdates,
      buildQueryToken,
      mapSortToFirestoreField,
      sortOrder,
    ]
  );

  const hydrateAlgoliaHits = useCallback(
    async (hits) => {
      const ids = hits.map((hit) => hit.id || hit.objectID).filter(Boolean);

      if (ids.length === 0) return [];

      const now = new Date();
      const chunks = [];
      for (let i = 0; i < ids.length; i += 10) {
        chunks.push(ids.slice(i, i + 10));
      }

      const docMap = new Map();

      for (const chunk of chunks) {
        const snapshot = await getDocs(
          query(collection(db, "projects"), where(documentId(), "in", chunk))
        );

        const hydratedChunk = await Promise.all(
          snapshot.docs.map(async (projectDoc) => {
            const raw = { id: projectDoc.id, ...projectDoc.data() };
            return applyStatusUpdates(raw, now);
          })
        );

        hydratedChunk.forEach((hydrated) => {
          if (hydrated?.id) {
            docMap.set(hydrated.id, hydrated);
          }
        });
      }

      return ids
        .map((id, index) => {
          if (docMap.has(id)) return docMap.get(id);
          const fallback = hits[index] || {};
          return { id, ...fallback };
        })
        .filter(Boolean);
    },
    [applyStatusUpdates]
  );

  const fetchDisjunctiveFacetCounts = useCallback(
    async (queryString, queryToken, fallbackFacets = {}) => {
      if (!algoliaClient) return;
      const currentLocalFacetMetadata = localFacetMetadataRef.current;

      try {
        const facetPromises = ALGOLIA_FACETS.map((facet) => {
          const filtersWithoutFacet = buildCombinedFilters(facet);
          return algoliaClient.searchSingleIndex({
            indexName: currentAlgoliaIndex,
            searchParams: {
              query: queryString,
              page: 0,
              hitsPerPage: 0,
              facets: [facet],
              maxValuesPerFacet: 500,
              ...(filtersWithoutFacet ? { filters: filtersWithoutFacet } : {}),
            },
          });
        });

        const results = await Promise.allSettled(facetPromises);
        const nextCounts = results.reduce((acc, result, index) => {
          const facetName = ALGOLIA_FACETS[index];
          if (result.status === "fulfilled") {
            acc[facetName] = result.value?.facets?.[facetName] || {};
          }
          return acc;
        }, {});

        const countsToApply = normalizeFacetCounts(
          Object.keys(nextCounts).length > 0 ? nextCounts : fallbackFacets
        );

        if (countsToApply && activeQueryTokenRef.current === queryToken) {
          const nextOptions = optionsFromFacets(countsToApply, filters);
          setAvailableFilterOptions((prev) => ({
            ...nextOptions,
            country:
              currentLocalFacetMetadata?.options.country || prev.country || [],
            taskType:
              currentLocalFacetMetadata?.options.taskType || prev.taskType || [],
          }));
          setFilterCounts((prev) => ({
            ...countsToApply,
            country:
              currentLocalFacetMetadata?.counts.country || prev.country || {},
            taskType:
              currentLocalFacetMetadata?.counts.taskType || prev.taskType || {},
          }));
        }
      } catch (err) {
        console.error("Failed to fetch disjunctive facets from Algolia:", err);
        if (fallbackFacets && activeQueryTokenRef.current === queryToken) {
          const nextOptions = optionsFromFacets(fallbackFacets, filters);
          setAvailableFilterOptions((prev) => ({
            ...nextOptions,
            country:
              currentLocalFacetMetadata?.options.country || prev.country || [],
            taskType:
              currentLocalFacetMetadata?.options.taskType || prev.taskType || [],
          }));
          setFilterCounts((prev) => ({
            ...fallbackFacets,
            country:
              currentLocalFacetMetadata?.counts.country || prev.country || {},
            taskType:
              currentLocalFacetMetadata?.counts.taskType || prev.taskType || {},
          }));
        }
      }
    },
    [algoliaClient, buildCombinedFilters, currentAlgoliaIndex, filters]
  );

  const refreshFacetCountsNow = useCallback(async () => {
    if (!isFilterPanelOpenRef.current) return;

    if (requiresLocalFiltering) {
      const allProjects =
        allProjectsForFiltersRef.current || (await ensureAllProjectsForFilters());
      const { counts, options } = buildProjectFilterMetadata(
        allProjects,
        filters,
        debouncedSearch
      );
      needsFacetRefreshRef.current = false;
      setFilterCounts(counts);
      setAvailableFilterOptions(options);
      return;
    }

    if (!algoliaClient) return;

    const queryToken = activeQueryTokenRef.current || buildQueryToken();
    const queryString =
      debouncedSearch.trim().length === 0 ? "" : debouncedSearch;

    needsFacetRefreshRef.current = false;
    fetchDisjunctiveFacetCounts(
      queryString,
      queryToken,
      filterCountsRef.current || {}
    );
  }, [
    algoliaClient,
    buildQueryToken,
    debouncedSearch,
    ensureAllProjectsForFilters,
    fetchDisjunctiveFacetCounts,
    filters,
    requiresLocalFiltering,
  ]);

  useEffect(() => {
    refreshFacetCountsNowRef.current = refreshFacetCountsNow;
  }, [refreshFacetCountsNow]);

  const fetchAlgoliaPage = useCallback(
    async (pageToLoad = 0, queryTokenParam = null, isInitialLoad = false) => {
      if (!algoliaClient) return;

      // Build filter strings from the filters state
      const allFilters = buildCombinedFilters();

      const queryToken = queryTokenParam || buildQueryToken();
      const isCurrentToken = activeQueryTokenRef.current === queryToken;
      if (isCurrentToken) {
        setError("");
        if (isInitialLoad) {
          setIsLoading(true);
          setIsLoadingMore(false);
        }
      }

      try {
        const queryString =
          debouncedSearch.trim().length === 0 ? "" : debouncedSearch;

        const res = await algoliaClient.searchSingleIndex({
          indexName: currentAlgoliaIndex,
          searchParams: {
            query: queryString,
            page: pageToLoad,
            hitsPerPage: PAGE_SIZE,
            facets: ALGOLIA_FACETS,
            maxValuesPerFacet: 500,
            ...(allFilters ? { filters: allFilters } : {}),
          },
        });

        const normalizedHits = (res?.hits || []).map((hit) => ({
          id: hit.id || hit.objectID,
          ...hit,
        }));

        const hydrated = await hydrateAlgoliaHits(normalizedHits);

        if (activeQueryTokenRef.current === queryToken) {
          setProjects((prev) =>
            pageToLoad === 0 ? hydrated : [...prev, ...hydrated]
          );
          algoliaPageRef.current = pageToLoad;

          const hasNext =
            typeof res?.nbPages === "number"
              ? pageToLoad + 1 < res.nbPages
              : false;
          setHasMore(hasNext);
        }

        if (
          isFilterPanelOpenRef.current &&
          needsFacetRefreshRef.current &&
          activeQueryTokenRef.current === queryToken
        ) {
          needsFacetRefreshRef.current = false;
          fetchDisjunctiveFacetCounts(
            queryString,
            queryToken,
            res?.facets || {}
          );
        }
      } catch (err) {
        console.error("Failed to search projects with Algolia:", err);
        if (activeQueryTokenRef.current === queryToken) {
          setError("Failed to search projects.");
        }
      } finally {
        if (activeQueryTokenRef.current === queryToken) {
          setIsLoading(false);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        }
      }
    },
    [
      algoliaClient,
      buildCombinedFilters,
      buildQueryToken,
      currentAlgoliaIndex,
      hydrateAlgoliaHits,
      fetchDisjunctiveFacetCounts,
      debouncedSearch,
    ]
  );

  const fetchProjects = useCallback(() => {
    const queryToken = buildQueryToken();
    activeQueryTokenRef.current = queryToken;
    resetPagination();
    if (requiresLocalFiltering) {
      fetchLocalFilteredPage(queryToken, true);
    } else if (useAlgolia) {
      fetchAlgoliaPage(0, queryToken, true);
    } else {
      fetchFirestorePage(null, queryToken, true);
      if (isFilterPanelOpenRef.current && needsFacetRefreshRef.current) {
        refreshFacetCountsNowRef.current?.();
      }
    }
  }, [
    buildQueryToken,
    fetchAlgoliaPage,
    fetchFirestorePage,
    fetchLocalFilteredPage,
    requiresLocalFiltering,
    resetPagination,
    useAlgolia,
  ]);

  useEffect(() => {
    needsFacetRefreshRef.current = true;
  }, [filters]);

  useEffect(() => {
    if (!isFilterPanelOpenRef.current) return;
    needsFacetRefreshRef.current = true;
  }, [debouncedSearch]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSearchChange = useCallback((event) => {
    const newSearch = event.target.value;
    setSearch(newSearch);
  }, []);

  const clearSearch = useCallback(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    setSearch("");
    setDebouncedSearch("");
  }, []);

  const handleCheckboxChange = useCallback((event, id) => {
    setSelectedProjects((prevSelected) =>
      event.target.checked
        ? [...prevSelected, id]
        : prevSelected.filter((projectId) => projectId !== id)
    );
  }, []);

  const handleInputChange = useCallback(
    (event, projectId, field, taskIndex = null) => {
      const value = event.target?.value || null;
      setProjects((prevProjects) => {
        const updatedProjects = prevProjects.map((project) => {
          if (project.id !== projectId) return project;

          if (taskIndex === null) {
            const updatedProject = { ...project, [field]: value };
            if (field === "dateForInvoice") {
              updatedProject.invoiceRaised = deriveInvoiceRaised(
                value,
                project.invoiceRaised
              );
            }
            const recalculatedStatuses = evaluateProjectStatuses(updatedProject);
            const mergedProject = {
              ...updatedProject,
              tasks:
                recalculatedStatuses.updatedTasks?.length > 0
                  ? recalculatedStatuses.updatedTasks
                  : updatedProject.tasks || [],
              invoiceRaised: recalculatedStatuses.invoiceRaised,
              paymentStatus: recalculatedStatuses.paymentStatus,
              projectStatus: recalculatedStatuses.projectStatus,
            };
            const numericFields = AMOUNT_FIELD_NAMES.has(field)
              ? buildNumericAmountFields(mergedProject)
              : {};
            return { ...mergedProject, ...numericFields };
          }

          const updatedTasks = project.tasks.map((task, index) => {
            if (index !== taskIndex) return task;

            const updatedTask = { ...task, [field]: value };

            if (field === "income" || field === "vat") {
              const income =
                field === "income"
                  ? parseFloat(value)
                  : parseFloat(task.income);
              const vat =
                field === "vat" ? parseFloat(value) : parseFloat(task.vat);
              const amountIncVat = income + (income * vat) / 100;
              updatedTask.amountIncVat = amountIncVat.toFixed(2);
            }

            return updatedTask;
          });

          const updatedIncome = updatedTasks.reduce(
            (acc, task) => acc + parseFloat(task.income || 0),
            0
          );
          const updatedVat =
            updatedTasks.length > 0 ? updatedTasks[0].vat : "0";
          const updatedAmountIncVat = updatedTasks.reduce(
            (acc, task) => acc + parseFloat(task.amountIncVat || 0),
            0
          );

          const updatedProject = {
            ...project,
            tasks: updatedTasks,
            income: updatedIncome.toFixed(2),
            vat: updatedVat,
            amountIncVat: updatedAmountIncVat.toFixed(2),
          };
          const numericFields = buildNumericAmountFields(updatedProject);
          const recalculatedStatuses = evaluateProjectStatuses(updatedProject);

          const mergedProject = {
            ...updatedProject,
            tasks:
              recalculatedStatuses.updatedTasks?.length > 0
                ? recalculatedStatuses.updatedTasks
                : updatedTasks,
            invoiceRaised: recalculatedStatuses.invoiceRaised,
            paymentStatus: recalculatedStatuses.paymentStatus,
            projectStatus: recalculatedStatuses.projectStatus,
          };

          return {
            ...mergedProject,
            ...numericFields,
          };
        });

        return updatedProjects;
      });
    },
    []
  );

  const handleBlur = useCallback(
    async (projectId, field, taskIndex = null) => {
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;

      const recalculatedStatuses = evaluateProjectStatuses(project);

      if (taskIndex === null) {
        const numericFields = AMOUNT_FIELD_NAMES.has(field)
          ? buildNumericAmountFields(project)
          : {};
        const updates = {
          ...numericFields,
          [field]: project[field] || null,
          invoiceRaised: recalculatedStatuses.invoiceRaised,
          paymentStatus: recalculatedStatuses.paymentStatus || null,
          projectStatus: recalculatedStatuses.projectStatus,
        };

        await updateDoc(doc(db, "projects", projectId), updates);
      } else {
        const numericFields = buildNumericAmountFields(project);
        await updateDoc(doc(db, "projects", projectId), {
          tasks: project.tasks,
          income: project.income,
          vat: project.vat,
          amountIncVat: project.amountIncVat,
          ...numericFields,
          tasksSearchText: buildTasksSearchText(project.tasks),
          invoiceRaised: recalculatedStatuses.invoiceRaised,
          paymentStatus: recalculatedStatuses.paymentStatus || null,
          projectStatus: recalculatedStatuses.projectStatus,
        });
      }
    },
    [projects]
  );

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || isLoadingMoreRef.current) return;

    const queryToken = activeQueryTokenRef.current || buildQueryToken();

    if (requiresLocalFiltering) {
      if (!hasMore) return;
      const nextVisibleCount = projects.length + PAGE_SIZE;
      setProjects(localFilteredProjectsRef.current.slice(0, nextVisibleCount));
      setHasMore(localFilteredProjectsRef.current.length > nextVisibleCount);
      return;
    }

    if (useAlgolia) {
      if (!hasMore) return;
      const nextPage = algoliaPageRef.current + 1;
      algoliaPageRef.current = nextPage; // Increment ref immediately
      setIsLoadingMore(true); // Set loading state synchronously
      isLoadingMoreRef.current = true;
      return fetchAlgoliaPage(nextPage, queryToken, false);
    }

    if (!hasMore) return;
    setIsLoadingMore(true); // Set loading state synchronously
    isLoadingMoreRef.current = true;
    return fetchFirestorePage(lastDoc, queryToken, false);
  }, [
    buildQueryToken,
    fetchAlgoliaPage,
    fetchFirestorePage,
    hasMore,
    isLoading,
    isLoadingMore,
    lastDoc,
    projects.length,
    requiresLocalFiltering,
    useAlgolia,
  ]);

  return {
    projects,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    search,
    selectedProjects,
    filters,
    setFilters,
    availableFilterOptions,
    filterCounts,
    hasActiveFilters,
    sortColumn,
    sortOrder,
    setSortColumn,
    setSortOrder,
    setSelectedProjects,
    handleFilterChange,
    clearAllFilters,
    setIsFilterPanelOpen,
    refreshFacetCountsNow,
    handleSearchChange,
    clearSearch,
    handleCheckboxChange,
    handleInputChange,
    handleBlur,
    fetchProjects,
    loadMore,
    convertTimestampToDate,
    setSearch,
  };
};

export default useProjects;
